import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import {
  ExtendedInitiative,
  PreSaveInitiative,
  RelativeBusinessValueUnits,
} from '../../../api/initiative-client/initiative-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';

/**
 * Hook that provides initial values for an initiative form
 *
 * @param initiative - Optional extended initiative data to populate the form
 * @returns Partial PreSaveInitiative object with initial values
 * If initiative is provided, returns values from the initiative
 * Otherwise, returns default empty values
 */
const useInitialValues = (initiative?: ExtendedInitiative): Partial<PreSaveInitiative> => {
  if (initiative) {
    return {
      name: initiative.name,
      description: initiative.description,
      start_date: initiative.start_date,
      end_date: initiative.end_date,
      relative_business_value: initiative.relative_business_value,
      relative_business_value_unit: initiative.relative_business_value_unit,
    };
  }

  return {
    name: '',
    description: '',
    start_date: undefined,
    end_date: undefined,
    relative_business_value: undefined,
    relative_business_value_unit: RelativeBusinessValueUnits.numeric,
  };
};

/**
 * Hook that provides initial epics for an initiative form
 *
 * @param initiative - Optional extended initiative data to populate the form
 * @returns Array of epics or empty array if initiative is not provided
 */
const useInitialEpics = (initiative?: ExtendedInitiative): Epic[] | [] => {
  if (initiative) {
    return initiative.epics;
  }

  return [];
};

/**
 * Hook that provides search functionality for filtering epics
 *
 * @param epics - Array of epics to filter
 * @returns Object containing filtered epics and search handler
 */
const useEpicsSearch = (epics: Epic[]) => {
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 200),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const filteredEpics = useMemo(() => {
    if (!searchTerm.trim()) {
      return epics;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return epics.filter(
      (epic) => epic.title.toLowerCase().includes(lowerSearchTerm) || epic.name.toLowerCase().includes(lowerSearchTerm),
    );
  }, [epics, searchTerm]);

  const handleSearchChange = (value: string) => {
    debouncedSetSearchTerm(value);
  };

  return {
    filteredEpics,
    searchTerm,
    handleSearchChange,
  };
};

export { useEpicsSearch, useInitialEpics, useInitialValues };
